import { inject, Injectable } from '@angular/core';
import { ICanvasElement, ICanvasElementVisualColor, ICanvasElementVisualImage } from '@features/canvas/canvas.types';
import { LpConditionsService } from '@features/lp/lp-conditions/lp-conditions.service';
import { convertLpColorToCSSColor, convertLpVector } from '@shared/lp.utils';
import { add, zero } from '@shared/vector.utils';
import { Id } from '@typings/id';
import {
  LpCanvasElement,
  LpCanvasElementComponent,
  LpClickTarget,
  LpDraggable,
  LpDropTarget,
  LpPlaceableMedia,
  LpShape,
  LpVisualElement,
} from '@typings/lp/generated';
import { Vector2CanvasNormalized, Vector2ElementNormalized, Vector2Screen } from '@typings/vectors';

const BACKGROUND_UID = 'BACKGROUND' as Id;

@Injectable({
  providedIn: 'root',
})
export class LpCanvasService {
  readonly #lpConditionsService = inject(LpConditionsService);

  // TODO: figure out if there is a more intuitive way to handle this
  public subscribeToShowIfChanges() {
    this.#lpConditionsService.subscribe();
  }

  public async convertDraggables(draggables: LpDraggable[], size: Vector2Screen): Promise<ICanvasElement[]> {
    return (
      await this.convertVisualElements(
        draggables.map((d) => d.visualElement),
        size,
      )
    ).map((el, i) => {
      el.uid = draggables[i].uid as Id;
      el.draggable = {
        position: undefined,
        isSnapped: false,
      };
      return el;
    });
  }

  public async convertDropTargets(dropTargets: LpDropTarget[], size: Vector2Screen): Promise<ICanvasElement[]> {
    return (
      await this.convertVisualElements(
        dropTargets.map((t) => t.visualElement),
        size,
      )
    ).map((el, i) => {
      el.uid = dropTargets[i].uid as Id;
      el.isDragTarget = true;
      return el;
    });
  }

  public async convertClickTargets(clickTargets: LpClickTarget[], size: Vector2Screen): Promise<ICanvasElement[]> {
    return (
      await this.convertVisualElements(
        clickTargets.map((t) => t.visualElement),
        size,
      )
    ).map((el, i) => {
      el.uid = clickTargets[i].uid as Id;
      el.isClickable = true;
      return el;
    });
  }

  public convertBackgroundToElement(background: LpPlaceableMedia, size: Vector2CanvasNormalized): LpCanvasElement {
    const components: LpCanvasElementComponent[] = [];
    if (background.dataType === 'ImagePlaceableMedia') {
      if (!background.image) throw new Error(`ImagePlaceableMedia for background '${background.uid}' must have an image`);

      components.push({
        dataType: 'ImageCanvasComponent',
        image: background.image,
        uid: 'BACKGROUND-IMAGE',
        colorTint: background.color,
      });
    } else if (background.dataType === 'SolidColorPlaceableMedia') {
      components.push({
        dataType: 'SolidColorCanvasComponent',
        color: background.color,
        uid: 'BACKGROUND-COLOR',
      });
    } else throw new Error(`Placeable media '${background.dataType}' is not supported`);

    return {
      dataType: 'CanvasElement',
      uid: background.uid,
      components,
      pivot: { x: 0.5, y: 0.5 },
      position: { x: 0, y: 0 },
      shape: {
        dataType: 'Rectangle',
        size: { x: size[0], y: size[1] },
        uid: 'BACKGROUND-SHAPE',
      },
    };
  }

  public convertBackground(background: LpPlaceableMedia, size: Vector2CanvasNormalized): ICanvasElement {
    const visual = this.getVisual(background);
    if ('fit' in visual) {
      visual.fit = 'cover';
    }

    return {
      uid: BACKGROUND_UID,
      position: [0, 0] as Vector2CanvasNormalized,
      pivot: [0.5, 0.5] as Vector2ElementNormalized,
      size,
      visual,
    } satisfies ICanvasElement;
  }

  private getVisual(placeableMedia: LpPlaceableMedia): ICanvasElementVisualImage | ICanvasElementVisualColor {
    if (placeableMedia.dataType === 'ImagePlaceableMedia') {
      const url = placeableMedia.image?.url;
      if (url) {
        return {
          type: 'IMAGE',
          color: convertLpColorToCSSColor(placeableMedia.color),
          url,
          fit: 'contain',
        } satisfies ICanvasElementVisualImage;
      } else {
        return {
          type: 'COLOR',
          color: convertLpColorToCSSColor(placeableMedia.color),
        } satisfies ICanvasElementVisualColor;
      }
    }

    if (placeableMedia.dataType === 'SolidColorPlaceableMedia') {
      return {
        type: 'COLOR',
        color: convertLpColorToCSSColor(placeableMedia.color),
      } satisfies ICanvasElementVisualColor;
    }

    throw new Error(`Placeable media '${placeableMedia.dataType}' is not supported`);
  }

  public async convertVisualElements(visualElements: LpVisualElement[], size: Vector2Screen): Promise<ICanvasElement[]> {
    return (
      await Promise.all(
        visualElements.map(async (visualElement) => {
          return await this.convertVisualElement(visualElement, size);
        }),
      )
    ).filter((element) => element !== undefined);
  }

  private async convertVisualElement(visualElement: LpVisualElement, size: Vector2Screen): Promise<ICanvasElement | undefined> {
    if (visualElement.showIf) {
      const result = await this.#lpConditionsService.evaluate(visualElement.showIf);
      if (!result) return undefined;
    }

    const visual = this.getVisual(visualElement.media);
    const shapeProperties = this.getShapeProperties(visualElement.shape, size);

    return {
      uid: visualElement.uid as Id,
      position: add(convertLpVector<Vector2CanvasNormalized>(visualElement.position), shapeProperties.offset) as Vector2CanvasNormalized,
      pivot: convertLpVector<Vector2ElementNormalized>(visualElement.pivot),
      size: shapeProperties.size,
      speed: visualElement.speed,
      visual,
      isClickable: (visualElement.onClick?.actions.length ?? 0) > 0,
      text:
        visualElement.label.length > 0
          ? {
              text: visualElement.label,
              position: visualElement.labelPosition,
              style: visualElement.labelStyle,
            }
          : undefined,
    } satisfies ICanvasElement;
  }

  public getShapeProperties(shape: LpShape, canvasSize: Vector2Screen): { offset: Vector2CanvasNormalized; size: Vector2CanvasNormalized } {
    if (shape.dataType === 'Circle') {
      const ratio = canvasSize ? canvasSize[0] / canvasSize[1] : 1;
      const size = [shape.radius * 2, shape.radius * 2 * ratio] as Vector2CanvasNormalized;
      return {
        offset: [-0.5 * size[0], -0.5 * size[1]] as Vector2CanvasNormalized,
        size,
      };
    }

    if (shape.dataType === 'Rectangle') {
      return { offset: zero() as Vector2CanvasNormalized, size: convertLpVector<Vector2CanvasNormalized>(shape.size) };
    }

    shape satisfies never;
    throw new Error(`Shape '${(shape as any).dataType}' is not supported`);
  }
}
